import { defineStore } from 'pinia';
import TUKConfig from '../entity/tuk/TUKConfig';
import { Container } from 'typescript-ioc';
import TUKService from '../service/TUKService';
import { TUK_FULL_ACCESS_ROLES } from '../util/PluralVariabels';

interface TUKState {
    userConfig: TUKConfig | null;
    bendaharaConfigs: TUKConfig[];
    isLoading: boolean;
    error: any | null;
}

export const useTUKStore = defineStore('tuk', {
    state: (): TUKState => ({
        userConfig: null,
        bendaharaConfigs: [],
        isLoading: false,
        error: null,
    }),

    getters: {
        isAuthenticated: (state) => !!state.userConfig,

        isFullAccess: (state) => {
            const type = state.userConfig?.bendahara_type;
            return type ? TUK_FULL_ACCESS_ROLES.includes(type) : false;
        },

        allowedZones: (state) => {
            return state.userConfig?.wilayah_access || [];
        },

        bendaharaType: (state) => state.userConfig?.bendahara_type || null,
    },

    actions: {
        async fetchUserConfig(forceFn: boolean = false) {
            if (this.userConfig && !forceFn) {
                return;
            }

            this.isLoading = true;
            this.error = null;

            try {
                const tukService = Container.get(TUKService);
                const response = await tukService.getTukConfig();

                if (response && response.isSuccess) {
                    this.userConfig = response.getDeserializeResponse(new TUKConfig(), TUKConfig);
                } else {
                    this.error = "Failed to fetch configuration";
                }
            } catch (err) {
                this.error = err;
            } finally {
                this.isLoading = false;
            }
        },

        async fetchBendaharaConfigs() {
            this.isLoading = true;
            this.error = null;
            this.bendaharaConfigs = [];

            try {
                const tukService = Container.get(TUKService);
                const response = await tukService.getBendaharaConfigs();

                if (response && response.isSuccess) {
                    this.bendaharaConfigs = response.getDeserializeResponse([], TUKConfig);
                } else {
                    this.error = "Failed to fetch bendahara configurations";
                }
            } catch (err) {
                this.error = err;
            } finally {
                this.isLoading = false;
            }
        },

        async updateBendaharaConfig(id: string, data: TUKConfig) {
            this.isLoading = true;
            try {
                const tukService = Container.get(TUKService);
                const response = await tukService.updateBendaharaConfig(id, data);
                if (response && response.isSuccess) {
                    await this.fetchBendaharaConfigs();
                    return true;
                }
            } catch (err) {
                this.error = err;
            } finally {
                this.isLoading = false;
            }
            return false;
        },

        async toggleBendaharaStatus(id: string, currentStatus: boolean) {
            const config = this.bendaharaConfigs.find(c => (c.id as any) === id);
            if (!config) return false;

            const updatedConfig = new TUKConfig();
            updatedConfig.user_id = config.user_id;
            updatedConfig.is_active = !currentStatus;

            return await this.updateBendaharaConfig(id, updatedConfig);
        },

        reset() {
            this.userConfig = null;
            this.bendaharaConfigs = [];
            this.isLoading = false;
            this.error = null;
        }
    },
});
